import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Plus, StickyNote, Save } from "lucide-react";
import { http } from "@/lib/api";
import { tagColor } from "@/lib/uiHelpers";

const SUGGESTED = ["VIP", "Referred", "Follow-up", "Docs pending", "High budget", "Cold", "Repeat client"];

export default function NotesTagsPanel({ lead, onUpdate }) {
  const [notes, setNotes] = useState(lead.notes_internal || "");
  const [tags, setTags] = useState(lead.tags || []);
  const [newTag, setNewTag] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    setNotes(lead.notes_internal || "");
    setTags(lead.tags || []);
  }, [lead.id, lead.notes_internal, lead.tags]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const { data } = await http.patch(`/leads/${lead.id}/notes-tags`, { notes_internal: notes });
      onUpdate?.(data);
      toast.success("Notes saved");
    } catch (e) {
      toast.error("Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const commitTags = async (nextTags) => {
    setTags(nextTags);
    try {
      const { data } = await http.patch(`/leads/${lead.id}/notes-tags`, { tags: nextTags });
      onUpdate?.(data);
    } catch (e) {
      toast.error("Failed to update tags");
    }
  };

  const addTag = (raw) => {
    const t = raw.trim();
    if (!t) return;
    if (tags.includes(t)) return;
    commitTags([...tags, t]);
    setNewTag("");
  };

  const removeTag = (t) => commitTags(tags.filter((x) => x !== t));

  return (
    <div className="border-t px-6 py-5">
      <div className="text-xs uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-1.5">
        <StickyNote className="w-3 h-3" /> Notes & Tags (staff-only)
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {tags.map((t) => {
          const c = tagColor(t);
          return (
            <span
              key={t}
              data-testid={`tag-chip-${t.replace(/\s/g, '-').toLowerCase()}`}
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold"
              style={{ background: c.bg, color: c.text }}
            >
              {t}
              <button onClick={() => removeTag(t)} className="hover:opacity-70" aria-label={`Remove ${t}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        })}
        {tags.length === 0 && <span className="text-xs text-slate-400">No tags yet.</span>}
      </div>
      <div className="flex gap-2 mb-3">
        <input
          data-testid="tag-input"
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(newTag); } }}
          placeholder="Add tag and press Enter…"
          className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#0B1E3F]"
        />
        <button
          onClick={() => addTag(newTag)}
          data-testid="tag-add-btn"
          className="rounded-lg bg-[#0B1E3F] text-white px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"
          disabled={!newTag.trim()}
        >
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      <div className="flex flex-wrap gap-1 mb-4">
        {SUGGESTED.filter((s) => !tags.includes(s)).map((s) => (
          <button
            key={s}
            onClick={() => addTag(s)}
            className="text-[11px] px-2 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-500 hover:border-[#0B1E3F] hover:text-[#0B1E3F]"
          >
            + {s}
          </button>
        ))}
      </div>

      {/* Notes */}
      <textarea
        data-testid="notes-textarea"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={4}
        placeholder="Private notes only visible to staff…"
        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#0B1E3F] resize-none"
      />
      <div className="flex justify-end mt-2">
        <button
          onClick={saveNotes}
          disabled={savingNotes || notes === (lead.notes_internal || "")}
          data-testid="notes-save-btn"
          className="rounded-lg bg-[#0B1E3F] text-white px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"
        >
          <Save className="w-3 h-3" /> {savingNotes ? "Saving…" : "Save notes"}
        </button>
      </div>
    </div>
  );
}
